import React, { useMemo, useCallback, useState, useEffect } from 'react';
import styles from './SentencesSection.module.css';
import { LoaderIcon } from '../../../../components/Icons';

const SentencesSection = ({
  chapter,
  displayMode,
  chineseTextColor,
  englishTextColor,
  fontSize,
  lineHeight,
  sentenceFavorites,
  sentenceFavoriteLoading,
  sentenceNoteLoading,
  highlightSentenceId,
  backgroundColor,
  chapterWordSet,
  sentenceTimings = { en: {}, cn: {} },
  onSentenceFavorite,
  onSentenceNote,
  onWordClick,
  onSentenceClick,
  onSentencePronounce,
}) => {
  const allowWordClick = typeof onWordClick === 'function';
  const allowSentencePronounce = typeof onSentencePronounce === 'function';

  useEffect(() => {
    if (!chapterWordSet) {
      console.log('[SentencesSection] 未获取到收藏单词集');
      return;
    }

    if (typeof chapterWordSet.size === 'number' && typeof chapterWordSet.has === 'function') {
      console.log('[SentencesSection] 收藏单词(Set)', Array.from(chapterWordSet));
      return;
    }

    console.log('[SentencesSection] 收藏单词(非 Set)', chapterWordSet);
  }, [chapterWordSet]);

  const highlightFavoritedWords = useCallback(
    (text) => {
      if (!text || chapterWordSet.size === 0) return text;
      const parts = text.split(/(\b[\w'-]+\b)/);
      return parts.map((part, index) => {
        const normalized = part.replace(/[^A-Za-z'-]/g, '').toLowerCase();
        if (normalized && chapterWordSet.has(normalized)) {
          return (
            <span key={`favorited-${index}`} className={styles.favoritedWord}>
              {part}
            </span>
          );
        }
        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
      });
    },
    [chapterWordSet]
  );

  const sentences = useMemo(() => {
    const originalTexts = [];
    const originalInfo = [];
    const translationTexts = [];
    const translationInfo = [];

    const pushEntry = (targetTexts, targetMeta, text, meta, fallbackIndex) => {
      const value = (text ?? '').toString();
      if (!value.trim()) {
        return;
      }
      const index = targetTexts.length;
      const metaObj = typeof meta === 'object' && meta !== null ? meta : {};
      const rawId =
        metaObj.sentence_id ??
        metaObj.id ??
        metaObj.sentenceId ??
        fallbackIndex ??
        index;
      const numericId = Number(rawId);
      const sentenceId = Number.isFinite(numericId) ? numericId : rawId ?? index;
      const orderIndex =
        metaObj.order_index ??
        metaObj.sentence_index ??
        fallbackIndex ??
        index;
      const startTime = metaObj.start_time ?? metaObj.start ?? null;

      targetTexts.push(value);
      targetMeta.push({
        sentenceId,
        orderIndex,
        startTime,
        raw: metaObj,
      });
    };

    const parseContentSource = (source, targetTexts, targetMeta) => {
      if (!source) return;
      try {
        const data = JSON.parse(source);
        if (Array.isArray(data)) {
          data.forEach((item, idx) => {
            if (typeof item === 'string') {
              pushEntry(targetTexts, targetMeta, item, { sentence_id: idx + 1 }, idx);
            } else if (item) {
              const text =
                item.sentence_content ??
                item.content ??
                '';
              pushEntry(targetTexts, targetMeta, text, item, idx);
            }
          });
        } else if (data?.sentences && Array.isArray(data.sentences)) {
          data.sentences.forEach((item, idx) => {
            const text =
              item?.sentence_content ??
              item?.content ??
              '';
            pushEntry(targetTexts, targetMeta, text, item, idx);
          });
        } else {
          const text =
            typeof data === 'string'
              ? data
              : data?.sentence_content ??
                data?.content ??
                '';
          pushEntry(targetTexts, targetMeta, text, data, 0);
        }
      } catch (err) {
        source
          .split(/\n+/)
          .forEach((text, idx) => {
            pushEntry(
              targetTexts,
              targetMeta,
              text,
              { sentence_id: idx + 1, order_index: idx },
              idx
            );
          });
      }
    };

    parseContentSource(chapter?.content, originalTexts, originalInfo);
    parseContentSource(chapter?.content_trans, translationTexts, translationInfo);

    if (originalTexts.length === 0 && translationTexts.length === 0) {
      if (chapter?.content && chapter.content.trim()) {
        return [
          {
            sentenceId: 0,
            orderIndex: 0,
            originalText: chapter.content,
            translatedText: '',
            startTime: null,
          },
        ];
      }
      return [];
    }

    const max = Math.max(originalTexts.length, translationTexts.length);
    const combined = [];

    for (let i = 0; i < max; i += 1) {
      const originalText = originalTexts[i] || '';
      const translatedText = translationTexts[i] || '';
      if (!originalText && !translatedText) {
        continue;
      }
      const originalMeta = originalInfo[i] || {};
      const translationMeta = translationInfo[i] || {};
      const sentenceId =
        originalMeta.sentenceId ??
        translationMeta.sentenceId ??
        i;
      const orderIndex =
        originalMeta.orderIndex ??
        originalMeta.sentence_index ??
        translationMeta.orderIndex ??
        translationMeta.sentence_index ??
        i;
      const startTime =
        originalMeta.startTime ??
        translationMeta.startTime ??
        null;

      combined.push({
        sentenceId,
        orderIndex,
        originalText,
        translatedText,
        startTime,
      });
    }

    return combined;
  }, [chapter]);

  const getMapValue = (map, key) => {
    if (
      map &&
      key !== undefined &&
      key !== null &&
      Object.prototype.hasOwnProperty.call(map, key)
    ) {
      return map[key];
    }
    return undefined;
  };

  const handleWordLookup = useCallback(
    (sentenceId, word) => {
      if (!allowWordClick || !word) return;
      onWordClick?.(word, sentenceId);
    },
    [allowWordClick, onWordClick]
  );

  if (!sentences.length) {
    return <div className={styles.noContent}>暂无章节内容</div>;
  }

  const createTextStyle = (baseColor) => ({
    color: baseColor,
    fontSize: typeof fontSize === 'number' ? `${fontSize}px` : undefined,
    lineHeight: typeof lineHeight === 'number' ? lineHeight : undefined,
  });

  const chineseTextStyle = createTextStyle(chineseTextColor);
  const englishTextStyle = createTextStyle(englishTextColor);

  return (
    <div className={styles.container}>
      {sentences.map((item, index) => {
        const { sentenceId, originalText, translatedText, startTime } = item;
        const resolvedId =
          sentenceId !== undefined && sentenceId !== null ? sentenceId : index;
        const sentenceKey = String(resolvedId);
        const favoriteValue = getMapValue(sentenceFavorites, resolvedId) ?? getMapValue(sentenceFavorites, sentenceKey);
        const isCurrentlyFavorited = favoriteValue === true;
        const isLoading =
          getMapValue(sentenceFavoriteLoading, sentenceKey) === true ||
          getMapValue(sentenceFavoriteLoading, resolvedId) === true;
        const noteLoading =
          getMapValue(sentenceNoteLoading, sentenceKey) === true ||
          getMapValue(sentenceNoteLoading, resolvedId) === true;
        const highlightKey =
          highlightSentenceId !== null && highlightSentenceId !== undefined
            ? String(highlightSentenceId)
            : null;
        const isHighlighted = highlightKey !== null && String(resolvedId) === highlightKey;
        const sentenceStyle = backgroundColor ? { backgroundColor } : undefined;
        const showActions = true; // 始终显示操作按钮
        const englishTiming =
          getMapValue(sentenceTimings?.en, resolvedId) ??
          getMapValue(sentenceTimings?.en, sentenceKey);
        const chineseTiming =
          getMapValue(sentenceTimings?.cn, resolvedId) ??
          getMapValue(sentenceTimings?.cn, sentenceKey);
        const fallbackTiming =
          startTime !== null && startTime !== undefined
            ? { start: startTime, end: null }
            : null;
        const favoriteTiming = {
          en: englishTiming || fallbackTiming,
          cn: chineseTiming || null,
        };
        const hasPronounceTiming =
          allowSentencePronounce &&
          (favoriteTiming.en && favoriteTiming.en.start != null);

        const handleSentenceGroupClick = () => {
          onSentenceClick?.(resolvedId, {
            originalText,
            translatedText,
            startTime,
            orderIndex: item.orderIndex ?? index,
          });
        };

        const handleWordClick = (word) => {
          if (!allowWordClick || !word) return;
          handleWordLookup(resolvedId, word.trim());
        };

        const handlePronounceClick = (event) => {
          if (!allowSentencePronounce) return;
          event.stopPropagation();
          onSentencePronounce?.(resolvedId, {
            timings: { en: favoriteTiming.en },
            originalText,
            translatedText,
          });
        };

        const getWordEvents = (word) =>
          allowWordClick
            ? {
                onClick: (e) => {
                  e.stopPropagation();
                  handleWordClick(word);
                },
              }
            : {};

        const renderEnglishContent = () => {
          if (!translatedText) {
            if (!originalText) {
              return null;
            }
            return (
              <p
                className={`${styles.sentenceText} ${styles.noTranslation}`}
                style={englishTextStyle}
              >
                <em>暂无翻译</em>
              </p>
            );
          }

          return translatedText
            .trim()
            .split(/(\s+)/)
            .map((part, wordIndex) => {
              if (/^\s+$/.test(part)) {
                return part;
              }
              if (part.trim()) {
                const normalized = part
                  .replace(/[^A-Za-z'-]/g, '')
                  .toLowerCase();
                const isFavoritedWord = normalized && chapterWordSet.has(normalized);

                return (
                  <span
                    key={wordIndex}
                    className={`${styles.clickableWord} ${
                      isFavoritedWord ? styles.favoritedWord : ''
                    }`}
                    {...getWordEvents(part)}
                  >
                    {part}
                  </span>
                );
              }
              return part;
            });
        };

        const englishParagraph =
          displayMode === 'zh-only'
            ? null
            : (
                <p
                  key="en"
                  className={`${styles.sentenceText} ${styles.translatedText}`}
                  style={englishTextStyle}
                >
                  {renderEnglishContent()}
                </p>
              );

        const chineseParagraph =
          displayMode !== 'en-only' && originalText
            ? (
                <p
                  key="zh"
                  className={`${styles.sentenceText} ${styles.originalText}`}
                  style={chineseTextStyle}
                >
                  {highlightFavoritedWords(originalText.trim())}
                </p>
              )
            : null;

        const renderedParagraphs = (
          displayMode === 'en-zh' || displayMode === 'en-only'
            ? [englishParagraph, chineseParagraph]
            : [chineseParagraph, englishParagraph]
        ).filter(Boolean);

        const renderExpandedContent = () => (
          <div className={styles.sentenceExpandedRow}>
            <div className={styles.actionButtons}>
              <button
                className={`${styles.iconActionButton} ${
                  isCurrentlyFavorited ? styles.iconActionButtonActive : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSentenceFavorite?.(
                    resolvedId,
                    originalText,
                    translatedText,
                    favoriteTiming
                  );
                }}
                disabled={isLoading}
                title={isCurrentlyFavorited ? '取消收藏句子' : '收藏句子'}
                type="button"
                aria-label={isCurrentlyFavorited ? '取消收藏句子' : '收藏句子'}
              >
                {isLoading ? (
                  <LoaderIcon size={18} />
                ) : (
                  <span className="material-symbols-rounded">favorite</span>
                )}
              </button>
              <button
                className={styles.iconActionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onSentenceNote?.(resolvedId, originalText, translatedText);
                }}
                disabled={noteLoading}
                title="添加笔记"
                type="button"
                aria-label="添加笔记"
              >
                {noteLoading ? (
                  <LoaderIcon size={18} />
                ) : (
                  <span className="material-symbols-rounded">edit_note</span>
                )}
              </button>
              {allowSentencePronounce && (
                <button
                  className={styles.iconActionButton}
                  onClick={handlePronounceClick}
                  title="播放发音"
                  type="button"
                  disabled={!hasPronounceTiming}
                  aria-label="播放发音"
                >
                  <span className="material-symbols-rounded">volume_up</span>
                </button>
              )}
            </div>
            <div className={styles.sentenceContent}>
              {renderedParagraphs}
            </div>
          </div>
        );

        const renderCollapsedContent = () => (
          <div className={`${styles.sentenceRow} ${styles.collapsedRow}`}>
            {renderedParagraphs}
          </div>
        );

        const groupClassName = [
          styles.sentenceGroup,
          isHighlighted ? styles.highlighted : '',
          styles.clickableSentence,
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div
            key={sentenceKey}
            id={`sentence-${resolvedId}`}
            className={groupClassName}
            style={sentenceStyle}
            onClick={handleSentenceGroupClick}
            role={onSentenceClick ? 'button' : 'presentation'}
            tabIndex={onSentenceClick ? 0 : -1}
          >
            {showActions ? renderExpandedContent() : renderCollapsedContent()}
          </div>
        );
      })}
    </div>
  );
};

export default SentencesSection;
